/* PACKAGES */
import React, { useState } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';

/* COMPONENTS */
import Button from '../../components/Elements/Button/Button';

/* DATA */
import filterProducts from './filterProducts';
import { retrieveProductCache } from '../../actions/checkout';
import { updateFilter, rebaseFilter, updatePagination } from '../../actions/environment';

/* UTILS */
import { intersect } from '../../utils/array';
import { translate, prefix } from '../../utils/locale';

/* STYLES */
import s from './FilterTools.scss';

const FilterTools = ({ setup, currentPath, filterCategories, filter, productCache, siteWide = false, relevantBaseProducts = false, filteredProducts = false, ...props }) => {
	const isSiteWide = !!siteWide;
	const [filterOpen, setFilterOpen] = useState(isSiteWide);
	const filtering = productCache?.[setup.market]?.[setup.pricelist];
	if (!filtering) {
		props.retrieveProductCache();
		return null;
	}
	const { filterArrays, localized, products, filterDescriptions } = filtering;
	const isLocalized = setup.languages && Object.keys(setup.languages).length > 1 && localized[setup.language];
	const categories = isLocalized ? Object.keys(filterArrays.categories).reduce((acc, category) => {
		const categoryKey = isLocalized?.categories?.[category] ? isLocalized.categories[category].key : category;
		return ({ ...acc, [categoryKey]: [...filterArrays.categories[category]] });
	}, {}) : filterArrays.categories;

	const handleToggleFilter = () => {
		setFilterOpen(!filterOpen);
	};

	const handleCloseFilter = () => {
		setFilterOpen(false);
		if (siteWide) siteWide();
	};

	const handleFilterUpdate = (e) => {
		e.preventDefault();
		const action = e.currentTarget.getAttribute('href');
		const filterType = action.indexOf('#') === 0 ? action.slice(1, action.indexOf('=')) : 'categories';
		const filterValue = action.indexOf('#') === 0 ? action.slice(action.indexOf('=') + 1) : action.replace(`/${setup.language}/`, '');

		props.updateFilter([{ [filterType]: filterValue }], false, isSiteWide, setup, currentPath, isSiteWide);
		props.updatePagination(currentPath, 1);
	};

	const handleClearFilter = () => {
		props.rebaseFilter(setup, currentPath, isSiteWide);
		props.updatePagination(currentPath, 1);
	};

	const handleClearCloseFilter = () => {
		handleClearFilter();
		handleCloseFilter();
	};

	// If no relevantBaseProducts are passed function is being called site-wide.
	if (!relevantBaseProducts) {
		const baseProducts = filterArrays.categories[setup?.statics?.baseFilterCategory];
		const filteringReturn = filterProducts({ baseProducts, filterArrays, filter, products, categories });
		relevantBaseProducts = filteringReturn.relevantBaseProducts;
		filteredProducts = filteringReturn.filteredProducts;
	}

	// Last filter is special so that the user can select multiple filters.
	let relevantFilterProductsCat = [...relevantBaseProducts];
	let relevantFilterProductsLastCat = [...relevantBaseProducts];
	let lastFilter = '';

	// Restrict filters based on final filter in filteredBy.
	filter.filteredBy.slice(1).forEach((filterObj, i) => {
		lastFilter = Object.keys(filterObj)[0];
		relevantFilterProductsCat = intersect(relevantFilterProductsCat, filteredProducts[lastFilter]);
		if (i < filter.filteredBy.length - 2) relevantFilterProductsLastCat = intersect(relevantFilterProductsLastCat, filteredProducts[lastFilter]);
	});

	let relevantFilterProducts = [...relevantBaseProducts];
	let relevantFilterProductsLast = [...relevantBaseProducts];

	// Restrict filters based on final filter in filteredBy.
	filter.filteredBy.slice(0).forEach((filterObj, i) => {
		lastFilter = Object.keys(filterObj)[0];
		relevantFilterProducts = intersect(relevantFilterProducts, filteredProducts?.[lastFilter] || relevantBaseProducts);
		if (i < filter.filteredBy.length - 1) relevantFilterProductsLast = intersect(relevantFilterProductsLast, filteredProducts?.[lastFilter] || relevantBaseProducts);
	});

	const currentCategories = filterCategories.map((fC) => {
		const category = fC.category?.[0]?.value?.[setup.language] || fC.category?.[0]?.value || '';
		if (categories[category] && intersect(categories[category], relevantBaseProducts).length === 0) return null;

		const linkClasses = [s.section_group_item, s.section_group_item__category];
		if (filter.categories.indexOf(category) !== -1) linkClasses.push(s.section_group_item__selected);

		return (
			<a key={category} className={linkClasses.join(' ')} onClick={handleFilterUpdate} href={`#categories=${category}`}>
				<span>{ translate(setup, fC.title) }</span>
			</a>
		);
	});

	return (
		<div className={[s.filterTools, (filterOpen ? s.filterTools__open : null), (isSiteWide ? s.filterTools__siteWide : null)].join(' ')}>
			{ filterOpen && <button className={s.filterTools_screen} type="button" onClick={handleCloseFilter} value="filter" aria-label="Close filter background cover" /> }
			<div className={s.sections}>
				{
					isSiteWide && (
						<button className={s.filterTools_close} type="button" onClick={handleCloseFilter} value="filter" aria-label="Close filter button">
							<svg className={s.section_title_icon} xmlns="http://www.w3.org/2000/svg" width="28" height="26" viewBox="0 0 28 26">
								<g fill="none" fillRule="evenodd" transform="translate(1 1)">
									<line className={s.section_title_icon_crossTop} x2="26" y1="3" y2="3" stroke="#2B3337" strokeWidth="2" />
									<line className={s.section_title_icon_crossBottom} x2="26" y1="21" y2="21" stroke="#2B3337" strokeWidth="2" />
								</g>
							</svg>
						</button>
					)
				}
				<div className={s.section}>
					<button className={s.section_title} type="button" onClick={isSiteWide ? handleCloseFilter : handleToggleFilter} value="filter">
						{
							!siteWide && (
								<svg className={s.section_title_icon} xmlns="http://www.w3.org/2000/svg" width="28" height="26" viewBox="0 0 28 26">
									<g fill="none" fillRule="evenodd" transform="translate(1 1)">
										<circle cx="8" cy="3" r="3" fill="#2B3337" />
										<circle cx="19" cy="12" r="3" fill="#2B3337" />
										<circle cx="12" cy="21" r="3" fill="#2B3337" />
										<line className={s.section_title_icon_crossTop} x2="26" y1="3" y2="3" stroke="#2B3337" strokeWidth="2" />
										<line className={s.section_title_icon_middle} x2="26" y1="12" y2="12" stroke="#2B3337" strokeWidth="2" />
										<line className={s.section_title_icon_crossBottom} x2="26" y1="21" y2="21" stroke="#2B3337" strokeWidth="2" />
									</g>
								</svg>
							)
						}
						<span className={s.section_title_filter}>{ translate(setup, 'filterTitle') }</span>
					</button>
					<div className={s.section_groups}>
						<div className={[s.section_group, s.section_group__categories].join(' ')}>
							<span className={s.section_group_title}>{ translate(setup, 'filterCategory') }</span>
							{ currentCategories }
						</div>
						<div className={[s.section_group, s.section_group__sizes].join(' ')}>
							<span className={s.section_group_title}>{ translate(setup, 'filterSize') }</span>
							{
								Object.keys(filterArrays.sizes).map((size) => {
									const linkClasses = [s.section_group_item];
									if (intersect(filterArrays.sizes[size], (lastFilter === 'sizes' ? relevantFilterProductsLast : relevantFilterProducts)).length === 0) {
										linkClasses.push(s.section_group_item__disabled);
									}
									if (filter.sizes.indexOf(size) !== -1) linkClasses.push(s.section_group_item__selected);

									if (size.indexOf('---') !== -1) return <h6 key={size} className={s.section_group_seperator}>{ size.replace(/[-\s]+/, '') }</h6>;

									return (
										<a key={size} className={linkClasses.join(' ')} onClick={handleFilterUpdate} href={`#sizes=${size}`}>
											<span>{ filterDescriptions.sizes[size] }</span>
										</a>
									);
								})
							}
						</div>
					</div>
					{
						isSiteWide && (
							<div className={s.filterTools_footer}>
								<Link to={prefix(setup, setup?.statics?.baseFilterCategory)} onClick={handleClearCloseFilter}>{ translate(setup, 'filterSeeAllProducts') }</Link>
								<div className={s.filterTools_footer_actions}>
									<Button type="link" location={prefix(setup, setup?.statics?.baseFilterCategory)} clickEventHandler={handleCloseFilter}>{ translate(setup, 'filterGo') }</Button>
									<button className={s.filterTools_footer_actions_clearAll} type="button" onClick={handleClearFilter}>
										<span>{ translate(setup, 'filterClear') }</span>
									</button>
								</div>
							</div>
						)
					}
					<button className={s.section_groups_close} type="button" onClick={handleCloseFilter} value="filter">
						<svg xmlns="http://www.w3.org/2000/svg" width="20" height="13" viewBox="0 0 20 13"><polyline fill="none" stroke="#979797" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" points="2.842 2.74 10.342 10.24 17.842 2.74" /></svg>
					</button>
				</div>
			</div>
		</div>
	);
};

const mapState = ({ filter, productCache, environment }, props) => ({
	filterCategories: environment.filterCategories,
	productCache,
	filter,
	...props,
});

export default hot(module)(connect(mapState, { retrieveProductCache, updateFilter, rebaseFilter, updatePagination })(FilterTools));
